import { Species } from '@bondvet/types/booking';
import type { RootState } from 'core/redux/types';
import type { KnownPatient } from 'booking/hooks/usePatientsAndContactInfoQuery';
import { AgeUnit, Sex } from '@bondvet/types/animals';
import type { BookingFlowGraphQLDigitalWalkIn as DigitalWalkIn } from '@bondvet/types/digitalWalkIns';
import type { BookingStep, PetReproductiveHealthType } from './types';
import type { AppointmentData, Provider } from '../lib/types';

export function hasBookingBooted(state: RootState): boolean {
    return state.booking.booted;
}

export function isSubmitting(state: RootState): boolean {
    return state.booking.submitting;
}

export function getSteps(state: RootState): ReadonlyArray<BookingStep> {
    return state.booking.steps;
}

export function getClosestAllowedStep(targetStep: BookingStep) {
    return (state: RootState): BookingStep => {
        const firstInvalidIndex = state.booking.steps.findIndex(
            (item) => !state.booking.validatedSteps.has(item),
        );

        const targetIndex = state.booking.steps.findIndex(
            (item) => item === targetStep,
        );

        if (targetIndex === -1 || firstInvalidIndex === -1) {
            return state.booking.steps[0];
        }

        const resultIndex = Math.min(
            firstInvalidIndex,
            targetIndex,
            state.booking.steps.length - 1,
        );

        return state.booking.steps[resultIndex];
    };
}

export function getSessionId(state: RootState): string | undefined {
    return state.booking.sessionId;
}

export function getPet(state: RootState): string | undefined {
    return (state.booking.pet ?? '').trim();
}

export function getSpecies(state: RootState): Species | undefined {
    return state.booking.species;
}

export function getBreed(state: RootState): string {
    return state.booking.breed;
}

export function getKnowsExactBirthdate(state: RootState): boolean {
    return state.booking.knowsExactBirthdate;
}

export function getBirthdate(state: RootState): Date | null {
    return state.booking.birthdate;
}

export function getAge(state: RootState): number | undefined {
    return state.booking.age;
}

export function getAgeUnit(state: RootState): AgeUnit | null {
    return state.booking.ageUnit === null || state.booking.ageUnit === undefined
        ? null
        : AgeUnit[state.booking.ageUnit];
}

export function getSex(state: RootState): Sex | null {
    return state.booking.sex === null || state.booking.sex === undefined
        ? null
        : Sex[state.booking.sex];
}

export function getReproductiveHealth(
    state: RootState,
): PetReproductiveHealthType | null {
    return state.booking.reproductiveHealth ?? null;
}

export function getCityId(state: RootState): string {
    return state.booking.cityId;
}

export function getLocationId(state: RootState): string {
    return state.booking.locationId;
}

export function getDate(state: RootState): string {
    return state.booking.date;
}

export function getTime(state: RootState): string {
    return state.booking.time;
}

export function getIsNearbyAvailableSelected(state: RootState): boolean {
    return state.booking.isNearbyAvailableSelected;
}

export function getIsDigitalWalkIn(state: RootState): boolean {
    return state.booking.isDigitalWalkIn;
}

export function getProvider(state: RootState): Provider {
    return state.booking.provider;
}

export function getReason(state: RootState): string {
    return state.booking.reason;
}

export function getNotes(state: RootState): string {
    return state.booking.notes;
}

export function getFirstName(state: RootState): string {
    return (state.booking.firstName ?? '').trim();
}

export function getLastName(state: RootState): string {
    return (state.booking.lastName ?? '').trim();
}

export function getEmail(state: RootState): string {
    return (state.booking.email ?? '').trim();
}

export function getPhoneNumber(state: RootState): string {
    return state.booking.phoneNumber;
}

export function getSmsPreferences(state: RootState): boolean {
    return state.booking.smsPreferences;
}

export function getSmsPreferencesDirty(state: RootState): boolean {
    return state.booking.smsPreferencesDirty;
}

export function getPromoCode(state: RootState): string {
    return state.booking.promoCode;
}

export function getSetupIntentId(state: RootState): string | null {
    return state.booking.setupIntentId;
}

export function hasExistingCCDetails(state: RootState): boolean {
    return state.booking.hasExistingCCDetails;
}

export function isLoadingExistingCCDetails(state: RootState): boolean {
    return state.booking.isLoadingExistingCCDetails;
}

export function isNewClient(state: RootState): boolean | undefined {
    return state.booking.isNewClient;
}

export function getClientId(state: RootState): string {
    return state.booking.clientId;
}

export function getAnimalId(state: RootState): string {
    return state.booking.animalId;
}

export function getLast4(state: RootState): string {
    return state.booking.last4;
}

export function getKnownPatients(state: RootState): KnownPatient[] {
    return state.booking.knownPatients;
}

export function getAppointment(state: RootState): AppointmentData | null {
    return state.booking.appointment;
}

export function getDigitalWalkIn(state: RootState): DigitalWalkIn | null {
    return state.booking.digitalWalkIn;
}

export function getError(state: RootState): string | null {
    return state.booking.error;
}

export function clientRequiresCreditCard(state: RootState): boolean | null {
    return state.booking.clientRequiresCreditCard;
}
